import { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { useSubscription } from '../hooks/useSubscription';

function Settings({ user }) {
  const { isPremium, loading: subscriptionLoading } = useSubscription(user);
  const [theme, setTheme] = useState('default');
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  // Available themes
  const themes = [
    {
      id: 'default',
      name: 'Purple Galaxy',
      description: 'The classic Iron Path look',
      preview: 'from-slate-900 via-purple-900 to-slate-900',
      free: true
    },
    {
      id: 'blue-ocean',
      name: 'Blue Ocean',
      description: 'Deep ocean blues with teal accents',
      preview: 'from-slate-900 via-blue-900 to-slate-900',
      free: false
    },
    {
      id: 'red-forge',
      name: 'Red Forge',
      description: 'Fiery reds and oranges',
      preview: 'from-slate-900 via-red-900 to-slate-900',
      free: false
    },
    {
      id: 'green-forest',
      name: 'Green Forest',
      description: 'Natural greens and earthy tones',
      preview: 'from-slate-900 via-green-900 to-slate-900',
      free: false
    },
    {
      id: 'amber-sunset',
      name: 'Amber Sunset',
      description: 'Warm amber and gold hues',
      preview: 'from-slate-900 via-amber-900 to-slate-900',
      free: false
    },
    {
      id: 'dark-minimal',
      name: 'Dark Minimal',
      description: 'Pure dark elegance',
      preview: 'from-gray-950 via-gray-900 to-gray-950',
      free: false
    },
    {
      id: 'iron-path',
      name: '⚒️ Iron Path',
      description: 'Blacksmith forge with brushed steel and ember glow',
      preview: 'from-zinc-900 via-orange-600 to-zinc-900',
      free: false
    }
  ];

  // Load user's theme preference
  useEffect(() => {
    const loadTheme = async () => {
      if (!user?.id) return;

      try {
        const { data, error } = await supabase
          .from('user_profiles')
          .select('theme')
          .eq('user_id', user.id)
          .single();

        if (error && error.code !== 'PGRST116') {
          console.error('Error loading theme:', error);
          return;
        }

        if (data?.theme) {
          setTheme(data.theme);
          // Apply theme to document
          document.documentElement.setAttribute('data-theme', data.theme);
        }
      } catch (err) {
        console.error('Error loading theme:', err);
      }
    };

    loadTheme();
  }, [user?.id]);

  const handleThemeChange = async (newTheme) => {
    // Check if user has premium for non-free themes
    const selectedTheme = themes.find(t => t.id === newTheme);
    if (!selectedTheme.free && !isPremium) {
      setSaveMessage('Premium subscription required for this theme!');
      setTimeout(() => setSaveMessage(''), 3000);
      return;
    }

    setTheme(newTheme);
    setSaving(true);
    setSaveMessage('');

    try {
      // Update theme in database
      const { error } = await supabase
        .from('user_profiles')
        .update({ theme: newTheme })
        .eq('user_id', user.id);

      if (error) throw error;

      // Apply theme to document
      document.documentElement.setAttribute('data-theme', newTheme);

      // Notify App.js and any other components that theme has changed
      window.dispatchEvent(new CustomEvent('theme-changed', { 
        detail: { theme: newTheme } 
      }));

      setSaveMessage('Theme saved successfully!');
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (err) {
      console.error('Error saving theme:', err);
      setSaveMessage('Failed to save theme. Please try again.');
      setTimeout(() => setSaveMessage(''), 3000);
    } finally {
      setSaving(false);
    }
  };

  if (subscriptionLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto"></div>
          <p className="text-purple-300 mt-4">Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white/10 backdrop-blur-lg rounded-2xl shadow-xl border border-purple-500/30 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-purple-800 px-6 py-6">
          <h1 className="text-3xl font-bold text-white flex items-center space-x-3">
            <span>⚙️</span>
            <span>Settings</span>
          </h1>
          <p className="text-purple-100 mt-2">Customize your Iron Path experience</p>
        </div>

        {/* Settings Content */}
        <div className="p-6 space-y-8">
          {/* Account Info Section */}
          <div className="space-y-4">
            <h2 className="text-xl font-semibold text-white flex items-center space-x-2">
              <span>👤</span>
              <span>Account Information</span>
            </h2>
            <div className="bg-white/5 rounded-lg p-4 space-y-2">
              <div className="flex justify-between">
                <span className="text-purple-300">Email:</span>
                <span className="text-white font-medium">{user.email}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-purple-300">Subscription:</span>
                <span className={`font-medium ${isPremium ? 'text-yellow-400' : 'text-gray-400'}`}>
                  {isPremium ? '💎 Premium Member' : 'Free Tier'}
                </span>
              </div>
            </div>
          </div>

          {/* Theme Selection */}
          <div className="space-y-4">
            <h2 className="text-xl font-semibold text-white flex items-center space-x-2">
              <span>🎨</span>
              <span>Theme</span>
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {themes.map((themeOption) => {
                const isSelected = theme === themeOption.id;
                const isLocked = !themeOption.free && !isPremium;
                
                return (
                  <button
                    key={themeOption.id}
                    onClick={() => !isLocked && handleThemeChange(themeOption.id)}
                    disabled={isLocked || saving}
                    className={`relative p-4 rounded-xl border-2 transition-all duration-200 text-left ${
                      isSelected
                        ? 'border-purple-500 bg-purple-500/20'
                        : 'border-white/20 bg-white/5 hover:border-purple-400 hover:bg-white/10'
                    } ${isLocked ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                  >
                    {/* Lock Icon for Premium Themes */}
                    {isLocked && (
                      <div className="absolute top-2 right-2">
                        <span className="text-2xl">🔒</span>
                      </div>
                    )}

                    {/* Selected Checkmark */}
                    {isSelected && (
                      <div className="absolute top-2 right-2">
                        <span className="text-2xl">✓</span>
                      </div>
                    )}

                    {/* Theme Preview */}
                    <div className={`h-16 rounded-lg bg-gradient-to-r ${themeOption.preview} mb-3`}></div>

                    {/* Theme Info */}
                    <div>
                      <h3 className="text-white font-semibold flex items-center space-x-2">
                        <span>{themeOption.name}</span>
                        {!themeOption.free && (
                          <span className="text-xs text-yellow-400">💎</span>
                        )}
                      </h3>
                      <p className="text-purple-300 text-sm mt-1">{themeOption.description}</p>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Save Message */}
            {saveMessage && (
              <div className={`text-center py-3 px-4 rounded-lg ${
                saveMessage.includes('success') 
                  ? 'bg-green-500/20 text-green-300 border border-green-500/30' 
                  : saveMessage.includes('required')
                  ? 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/30'
                  : 'bg-red-500/20 text-red-300 border border-red-500/30'
              }`}>
                {saveMessage}
              </div>
            )}
          </div>

          {/* Upgrade CTA for Free Users */}
          {!isPremium && (
            <div className="bg-gradient-to-r from-purple-600/20 to-pink-600/20 rounded-lg p-6 border border-purple-500/30">
              <div className="flex items-start space-x-4">
                <span className="text-4xl">💎</span>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-white mb-2">Unlock Premium Themes</h3>
                  <p className="text-purple-200 mb-4">
                    Get access to exclusive themes and customize your Iron Path experience. 
                    Plus unlimited AI analysis, full workout history, and more!
                  </p>
                  <button
                    onClick={() => {
                      window.dispatchEvent(new Event('navigate-to-subscription'));
                    }}
                    className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-200 shadow-lg hover:shadow-xl"
                  >
                    View Premium Plans
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Settings;